import {
  createFileRoute,
  Link,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";
import {
  hasActiveSession,
  sendUserEmailVerification,
  verifyUserEmailAttribute,
} from "@/lib/auth";
import { fetchAuthSession, fetchUserAttributes } from "@aws-amplify/auth";
import {
  getRedirect,
  clearRedirect,
  getMostRecentRoute,
  clearMostRecentRoute,
  savePendingEmailVerification,
  emailsMatch,
} from "@/lib/storage-utils";
import { useLogout } from "@/components/providers/ApiProvider/hooks";
import { authTokens } from "@/lib/tokens";
import { Button, Heading, Text } from "@radix-ui/themes";
import { useEffect, useState } from "react";

const FALLBACK_ROUTE = "/audiolab";

export const Route = createFileRoute("/auth/_authLayout/verify-email")({
  component: RouteComponent,
  head: () => ({ meta: [{ title: "Verify Email | Lutely" }] }),
});

type LoadingState = "verifying" | "redirecting";
type ErrorState =
  | "expired"
  | "mismatch"
  | "wrong-account"
  | "already-verified"
  | "generic";

function RouteComponent() {
  const location = useLocation();
  const navigate = useNavigate();
  const logoutUser = useLogout();
  const [loadingState, setLoadingState] = useState<LoadingState>("verifying");
  const [verificationError, setVerificationError] = useState<ErrorState | null>(
    null,
  );
  const [resentEmail, setResentEmail] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendError, setResendError] = useState(false);
  // Only meaningful once a "wrong-account" error is set.
  const [linkEmail, setLinkEmail] = useState<string | null>(null);
  const [sessionEmail, setSessionEmail] = useState<string | null>(null);
  const [pendingCode, setPendingCode] = useState<string | null>(null);
  // Resend mints a code for the signed-in user, so it stays hidden unless
  // this link belongs to them.
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    const runVerification = async () => {
      const urlParams = new URLSearchParams(location.searchStr);
      const code = urlParams.get("code");
      // Absent on older links, which keep the previous behavior.
      const intendedEmail = urlParams.get("email");

      if (!code) {
        setVerificationError("generic");
        return;
      }

      const isAuth = await hasActiveSession();
      if (!isAuth) {
        setLoadingState("redirecting");
        savePendingEmailVerification(code, intendedEmail);
        navigate({
          to: "/",
          search: {
            auth: "login",
            "info-toast": "Sign in to complete your email verification.",
          },
          replace: true,
        });
        return;
      }

      // Cognito applies the code to whichever session presents it, so a link
      // opened in a browser signed in as someone else would verify the wrong
      // account. Refuse before calling Cognito at all.
      let currentEmail: string | null = null;
      try {
        currentEmail = (await fetchUserAttributes()).email ?? null;
      } catch (err) {
        // The session can lapse between hasActiveSession() and here.
        console.error("Could not read the current session:", err);
        setVerificationError("generic");
        return;
      }

      setSessionEmail(currentEmail);
      setPendingCode(code);

      if (intendedEmail && !emailsMatch(intendedEmail, currentEmail)) {
        setLinkEmail(intendedEmail);
        setVerificationError("wrong-account");
        return;
      }

      setCanResend(true);

      try {
        await verifyUserEmailAttribute(code);
        await fetchAuthSession({ forceRefresh: true });

        const redirectLocation = getRedirect();
        const mostRecentLocation = getMostRecentRoute();

        let finalRedirectPath = FALLBACK_ROUTE;
        let search = {};

        if (redirectLocation?.pathname) {
          finalRedirectPath = redirectLocation.pathname;
          search = redirectLocation.search || {};
          clearRedirect();
        } else if (mostRecentLocation) {
          finalRedirectPath = mostRecentLocation.pathname;
          search = mostRecentLocation.search || {};
          clearMostRecentRoute();
        }

        navigate({
          to: finalRedirectPath,
          search: (prev: Record<string, unknown>) => ({
            ...prev,
            ...search,
            "success-toast": "Your email has been verified successfully.",
          }),
          replace: true,
          reloadDocument: true,
        });
      } catch (err) {
        const error = err as { name?: string };
        console.error("Verification failed:", err);
        if (error.name === "ExpiredCodeException") {
          setVerificationError("expired");
        } else if (error.name === "CodeMismatchException") {
          setVerificationError("mismatch");
        } else if (error.name === "NotAuthorizedException") {
          setVerificationError("already-verified");
        } else {
          setVerificationError("generic");
        }
      }
    };

    runVerification();
  }, []);

  if (verificationError) {
    return (
      <div className={authTokens.page}>
        <div
          className={`${authTokens.card} flex flex-col items-center gap-4 text-center`}
        >
          {verificationError === "wrong-account" && (
            <>
              <Heading
                as="h1"
                weight="bold"
                className="font-figtree text-[28px] leading-[1.2] tracking-[-0.28px] sm:text-[32px]"
              >
                Wrong account
              </Heading>
              <Text
                size="2"
                className="text-gray-700 text-[14px] sm:text-[15px]"
              >
                This link verifies <strong>{linkEmail}</strong>, but you're
                signed in as <strong>{sessionEmail}</strong>. Sign out and sign
                back in as {linkEmail} to finish verifying.
              </Text>
              <Button
                variant="solid"
                onClick={() => {
                  // Let the post-login replay resume this after they sign in.
                  if (pendingCode) {
                    savePendingEmailVerification(pendingCode, linkEmail);
                  }
                  logoutUser();
                }}
              >
                Sign out and switch account
              </Button>
              <Link
                to={FALLBACK_ROUTE}
                className="text-sm text-gray-500 underline"
              >
                Go to Audiolab
              </Link>
            </>
          )}
          {(verificationError === "expired" ||
            verificationError === "mismatch") && (
            <>
              <Heading
                as="h1"
                weight="bold"
                className="font-figtree text-[28px] leading-[1.2] tracking-[-0.28px] sm:text-[32px]"
              >
                {verificationError === "expired"
                  ? "Link expired"
                  : "Code not recognized"}
              </Heading>
              <Text
                size="2"
                className="text-gray-700 text-[14px] sm:text-[15px]"
              >
                {verificationError === "expired"
                  ? "This verification link has expired or been replaced by a newer one. Request a new one below."
                  : "This code doesn't match the one we're expecting. It may belong to a different account, or a newer link may have replaced it."}
              </Text>
              {canResend &&
                (resentEmail ? (
                  <Text size="2" className="text-green-600 text-[14px]">
                    Verification email sent! Check your inbox.
                  </Text>
                ) : (
                  <Button
                    variant="solid"
                    disabled={isResending}
                    onClick={async () => {
                      setIsResending(true);
                      setResendError(false);
                      try {
                        await sendUserEmailVerification();
                        setResentEmail(true);
                      } catch (err) {
                        console.error(
                          "Failed to resend verification email:",
                          err,
                        );
                        setResendError(true);
                      } finally {
                        setIsResending(false);
                      }
                    }}
                  >
                    Resend verification email
                  </Button>
                ))}
              {resendError && (
                <Text size="2" className="text-red-600 text-[14px]">
                  Couldn't send the email. Please try again.
                </Text>
              )}
              <Link
                to={FALLBACK_ROUTE}
                className="text-sm text-gray-500 underline"
              >
                Go to Audiolab
              </Link>
            </>
          )}
          {verificationError === "already-verified" && (
            <>
              <Heading
                as="h1"
                weight="bold"
                className="font-figtree text-[28px] leading-[1.2] tracking-[-0.28px] sm:text-[32px]"
              >
                Already verified
              </Heading>
              <Text
                size="2"
                className="text-gray-700 text-[14px] sm:text-[15px]"
              >
                Your email address has already been verified.
              </Text>
              <Button asChild variant="solid">
                <Link to={FALLBACK_ROUTE}>Go to Audiolab</Link>
              </Button>
            </>
          )}
          {verificationError === "generic" && (
            <>
              <Heading
                as="h1"
                weight="bold"
                className="font-figtree text-[28px] leading-[1.2] tracking-[-0.28px] sm:text-[32px]"
              >
                Verification failed
              </Heading>
              <Text
                size="2"
                className="text-gray-700 text-[14px] sm:text-[15px]"
              >
                Something went wrong while verifying your email. Please try
                clicking the link in your email again or contact support.
              </Text>
              <Link
                to={FALLBACK_ROUTE}
                className="text-sm text-gray-500 underline"
              >
                Go to Audiolab
              </Link>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={authTokens.page}>
      <div
        className={`${authTokens.card} flex flex-col items-center gap-4 text-center`}
        role="status"
        aria-live="polite"
      >
        <Heading
          as="h1"
          weight="bold"
          className="font-figtree text-[28px] leading-[1.2] tracking-[-0.28px] sm:text-[32px]"
        >
          {loadingState === "redirecting"
            ? "Redirecting to login…"
            : "Verifying your email"}
        </Heading>
        <Text size="2" className="text-gray-700 text-[14px] sm:text-[15px]">
          {loadingState === "redirecting"
            ? "You’ll need to log in first. Your verification link will be applied automatically after you sign in."
            : "Hang tight—this only takes a moment. You’ll be redirected as soon as we confirm your account."}
        </Text>
        <div className="flex items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-r-transparent" />
          <span className="sr-only">
            {loadingState === "redirecting"
              ? "Redirecting to login"
              : "Verifying email"}
          </span>
        </div>
      </div>
    </div>
  );
}
